import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getSuperAdminBranding, loginUserAdmin } from '../../service/endpoints';
import '../../Auth.css';
import {
  getStoredSuperadminBranding,
  persistSuperadminBranding,
} from '../../utils/superadminBranding';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(getStoredSuperadminBranding());

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const res = await getSuperAdminBranding();
        if (res?.success) {
          const nextBranding = persistSuperadminBranding(res.data);
          setBranding(nextBranding);
        }
      } catch (error) {
        // Keep cached branding if public branding fetch fails.
      }
    };

    loadBranding();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error('Email/username and password are required.');
      return;
    }
    setLoading(true);
    try {
      const res = await loginUserAdmin({ username: username.trim(), password });
      // Backend body: { success, statusCode, message, data: { message, access_token, refresh_token, role, user } }
      if (res?.success && res?.data) {
        sessionStorage.setItem('token', res.data.access_token);
        if (res.data.refresh_token) sessionStorage.setItem('refresh_token', res.data.refresh_token);
        sessionStorage.setItem('userdata', res.data.role);
        sessionStorage.setItem('userdetails', JSON.stringify(res.data.user ?? res.data.user_details ?? {}));
        toast.success(res?.message ?? res.data?.message ?? 'Logged in successfully');
        // Full reload so App.js re-checks auth and renders SuperAdmin; "/" is Dashboard
        setTimeout(() => window.location.replace('/'), 500);
      } else {
        toast.error(res?.message ?? res?.error?.message ?? res?.data?.error_msg ?? 'Login failed');
        setLoading(false);
      }
    } catch (err) {
      const msg = err?.error_msg ?? err?.message ?? 'Login failed. Please try again.';
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid auth-page h-100 d-flex align-items-center justify-content-center">
      <div className="Login-card auth-card">
        <div className="card-body">
          <div className="text-center mb-4">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.name || 'Super Admin logo'}
                style={{ maxHeight: '58px', maxWidth: '180px', objectFit: 'contain' }}
              />
            ) : (
              <h2 className="mb-0 fw-bold">LMS</h2>
            )}
          </div>
          <h3 className="card-title mb-0"><span className="span"></span>Login</h3>
          <p className="auth-subtitle">Super Admin</p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="mb-3">
              <label className="form-label auth-label">Email or username</label>
              <input
                type="text"
                className="form-control auth-control"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Email or username"
                autoComplete="username"
              />
            </div>
            <div className="mb-3">
              <label className="form-label auth-label">Password</label>
              <div className="input-group auth-input-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control auth-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="input-group-text auth-eye-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.359 11.238 15.147 13.026l-.708.708-2.02-2.02A8.783 8.783 0 0 1 8 13C3 13 1 8 1 8a13.133 13.133 0 0 1 3.244-4.156L.146 1.746l.708-.708 14 14-.708.708-1.787-1.787zM5.172 4.464A5.9 5.9 0 0 0 2.22 8c.5.95 2.245 3.75 5.78 3.75 1.015 0 1.906-.23 2.665-.59l-1.124-1.124A2.5 2.5 0 0 1 5.965 6.46L5.172 5.667z"/>
                      <path d="M10.477 8.649 7.35 5.523a2 2 0 0 1 3.126 3.126zM8 3c5 0 7 5 7 5a13.133 13.133 0 0 1-2.004 2.82l-.743-.743A11.91 11.91 0 0 0 13.78 8c-.5-.95-2.245-3.75-5.78-3.75-.882 0-1.675.14-2.377.358l-.748-.748A8.783 8.783 0 0 1 8 3z"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.12 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.133 13.133 0 0 1 1.172 8z"/>
                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <p className="text-end small mb-2">
              <a href="/forgot-password" className="auth-link" style={{ cursor: 'pointer' }}>Forgot password?</a>
            </p>
            <button type="submit" className="button auth-btn py-2 w-100" disabled={loading}>
              {loading ? 'Signing in…' : 'Login'}
            </button>
          </form>

          <p className="text-center small mt-3 mb-0 auth-footer-link">
            <a href="/" className="text-secondary" style={{ cursor: 'pointer' }}>← Back</a>
          </p>
        </div>
      </div>
      <ToastContainer limit={2} position="top-center" />
    </div>
  );
};

export default Login;
